<template>
  <div
    ref="wrapperRef"
    class="m-input-wrapper"
    :class="{ 'm-input-wrapper--select-only-clear': showClearSuggesterButton }"
    @keydown.delete="onSelectOnlyDelete"
  >
    <template v-if="plain">
      <InputText
        v-if="!isMultipleMode"
        ref="inputRef"
        :id="uniqueId"
        v-bind="filteredProps"
        :class="inputClasses"
        :modelValue="modelValue"
        :readonly="readOnly"
        @update:modelValue="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.enter="onEnter($event)"
        @keydown.escape="onEscape"
      >
        <template v-if="$slots.prepend" #prepend>
          <slot name="prepend" />
        </template>

        <template v-if="$slots.append" #append>
          <slot name="append" />
        </template>
      </InputText>
      <!-- onClickMultiple: много значений через разделитель — растём по вертикали -->
      <Textarea
        v-else
        ref="inputRef"
        :id="uniqueId"
        v-bind="filteredProps"
        :class="inputClasses"
        :modelValue="String(modelValue ?? '')"
        :readonly="readOnly"
        :autoResize="true"
        :rows="1"
        @update:modelValue="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.enter="onEnter($event)"
        @keydown.escape="onEscape"
      />
    </template>
    <FloatLabel v-else variant="on">
      <InputMask
        v-if="props.mask && !isMultipleMode"
        ref="inputRef"
        :id="uniqueId"
        :mask="props.mask"
        v-bind="filteredProps"
        :class="inputClasses"
        :modelValue="modelValue"
        :readonly="readOnly"
        @update:modelValue="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.enter="onEnter($event)"
        @keydown.escape="onEscape"
      />
      <InputText
        v-else-if="!isMultipleMode"
        ref="inputRef"
        :id="uniqueId"
        v-bind="filteredProps"
        :class="inputClasses"
        :modelValue="modelValue"
        :readonly="readOnly"
        @update:modelValue="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.enter="onEnter($event)"
        @keydown.escape="onEscape"
      >
        <template v-if="$slots.prepend" #prepend>
          <slot name="prepend" />
        </template>

        <template v-if="$slots.append" #append>
          <slot name="append" />
        </template>
      </InputText>
      <!-- onClickMultiple: много значений через разделитель — растём по вертикали как в Swing (не однострочный input) -->
      <Textarea
        v-else
        ref="inputRef"
        :id="uniqueId"
        v-bind="filteredProps"
        :class="inputClasses"
        :modelValue="String(modelValue ?? '')"
        :readonly="readOnly"
        :autoResize="true"
        :rows="1"
        @update:modelValue="onInput"
        @focus="onFocus"
        @blur="onBlur"
        @keydown.enter="onEnter($event)"
        @keydown.escape="onEscape"
      />
      <label :for="uniqueId">{{ label || placeholder }}</label>
    </FloatLabel>
    <button
      v-if="showClearSuggesterButton"
      type="button"
      class="m-input-suggester-clear"
      tabindex="-1"
      :title="t('ui.clear')"
      @mousedown.prevent
      @click="clearSelectOnlySuggester"
    >
      <i class="pi pi-times" aria-hidden="true" />
    </button>
    <div v-if="tooltip" class="m-input-tooltip">
      <Button
        icon="pi pi-question-circle"
        class="p-button-text p-button-plain tooltip-button"
        @click="toggleTooltip"
      />
      <Popover ref="popover" :dismissable="true" class="tooltip-popover">
        <div class="tooltip-content">
          {{ tooltip }}
        </div>
      </Popover>
    </div>

    <div v-if="hint && (persistentHint || isFocused)" class="m-input-hint">
      {{ hint }}
    </div>

    <FieldVerifyErrorOverlay
      v-if="!errorHostedOutside"
      :messages="normalizedErrors"
      :anchor-el="wrapperRef"
    />

    <!-- Autocomplete: сетка -->
    <Teleport v-if="isAutocompleteEnabled" to="#ui-popup-root">
      <div
        v-if="showAutocompleteDropdown"
        ref="autocompleteDropdownRef"
        class="m-input-autocomplete-dropdown"
        :style="dropdownContainerStyle"
        @mousedown.prevent
      >
        <!-- Один скролл на всю высоту до края экрана: кнопка + сетка -->
        <div
          class="autocomplete-dropdown-body"
          :style="{ maxHeight: `${layout.panelMaxHeight}px` }"
        >
          <div
            v-if="showSelectAllRow"
            class="autocomplete-select-all"
            @mousedown.prevent
            @click.prevent="selectAll"
          >
            Выбрать все
          </div>
          <div class="autocomplete-grid" :style="autocompleteGridStyle">
            <div
              v-for="(suggestion, index) in suggestionsItems"
              :key="index"
              class="autocomplete-item"
              :class="{ 'autocomplete-item--selected': isSuggestionSelected(suggestion) }"
              @mousedown.prevent
              @click.prevent="selectSuggestion(suggestion)"
            >
              <span class="autocomplete-item-name">{{ suggestion.name }}</span>
              <span
                v-if="formatSuggestionExtra(suggestion.extra)"
                class="autocomplete-item-extra"
              >{{ formatSuggestionExtra(suggestion.extra) }}</span>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import Button from 'primevue/button'
import InputMask from 'primevue/inputmask'
import InputText from 'primevue/inputtext'
import Textarea from 'primevue/textarea'
import Popover from 'primevue/popover'
import {
  isAtMultipleSelectionLimit,
  isAutocompleteMultipleMode,
  isFinitePositiveMultipleLimit,
  isSelectOnlySuggester,
  shouldCloseAutocompleteAfterSelect,
} from '@/common/utils/mInputAutocompleteContract'
import { acceptTextFieldFormatInput } from '@/common/utils/textFieldFormat'
import { listboxOptionTextFromRow } from '@/common/utils/listboxTableResolve'
import FieldVerifyErrorOverlay from '@/components/ui/FieldVerifyErrorOverlay.vue'
import { FieldVerifyErrorHostKey } from '@/components/ui/fieldVerifyErrorHost'
import { useI18n } from '@/common/composables/useI18n'
import { computed, inject, nextTick, onMounted, onUnmounted, ref, useAttrs, useId, watch } from 'vue'

interface AutoCompleteSuggestion {
  name: string
  extra?: string
}

interface Props {
  modelValue?: string | number
  label?: string
  placeholder?: string
  hint?: string
  persistentHint?: boolean
  tooltip?: string
  disabled?: boolean
  readonly?: boolean
  clearable?: boolean
  type?: string
  mask?: string
  errorMessages?: string | string[]
  height?: string | number | null
  align?: 'left' | 'center' | 'right'
  autoComplete?: string
  enableSuggestEdit?: string
  multipleDelimiter?: string
  plain?: boolean
  /** Swing Text format — insert filter (`FormatAdapter.check`). */
  platformFormat?: string
  /** Лимит выбранных значений в multiple-режиме (Element.setMultipleLimit) */
  multipleLimit?: number | null
  suggestions?: AutoCompleteSuggestion[]
}

const props = withDefaults(defineProps<Props>(), {
  modelValue: '',
  label: '',
  placeholder: '',
  hint: '',
  persistentHint: false,
  tooltip: '',
  disabled: false,
  readonly: false,
  clearable: false,
  type: 'text',
  errorMessages: () => [],
  height: null,
  align: 'left',
  plain: false,
  platformFormat: '',
})
const emit = defineEmits<{
  'update:modelValue': [value: string | number]
  blur: []
  focus: []
  enter: [value: string | number]
  change: [value: string | number]
}>()

const attrs = useAttrs()
const errorHostedOutside = inject(FieldVerifyErrorHostKey, false)
const { t } = useI18n()
const tooltipPanel = ref()
const popover = ref()
const uniqueId = useId()
const showTooltip = ref(false)
const isFocused = ref(false)
const showDropdown = ref(false)
const autocompleteDropdownRef = ref<HTMLElement | null>(null)
const wrapperRef = ref<HTMLElement | null>(null)
let blurCloseTimer: ReturnType<typeof setTimeout> | null = null
const inputRef = ref<any>()

const filteredProps = computed(() => {
  const {
    modelValue,
    label,
    placeholder,
    hint,
    persistentHint,
    tooltip,
    readonly,
    mask,
    errorMessages,
    height,
    align,
    plain,
    platformFormat,
    ...rest
  } = props
  return { ...rest, ...attrs }
})

const readOnly = computed(() => props.readonly || props.enableSuggestEdit === 'false')

const isAutocompleteEnabled = computed(() => !!props?.autoComplete)

const isMultipleMode = computed(() => isAutocompleteMultipleMode(props.autoComplete))

const isSelectOnlyAutocomplete = computed(() =>
  isSelectOnlySuggester(props.autoComplete, props.enableSuggestEdit),
)

const showClearSuggesterButton = computed(() => {
  if (!isSelectOnlyAutocomplete.value || props.readonly || props.disabled) return false
  return String(props.modelValue ?? '').trim() !== ''
})

const inputClasses = computed(() => [
  'm-input',
  `text-align-${props.align}`,
  {
    'm-input--has-tooltip': props.tooltip,
    'm-input--disabled': props.disabled,
    'm-input--error': hasErrors.value,
    'm-input--autocomplete-multiple': isMultipleMode.value,
  },
])

const normalizedErrors = computed(() => {
  if (Array.isArray(props.errorMessages)) return props.errorMessages
  return [props.errorMessages]
})

const hasErrors = computed(() => normalizedErrors.value.length > 0)

const restoreNativeInputValue = (previous: string) => {
  const root = inputRef.value?.$el ?? inputRef.value
  const el =
    root instanceof HTMLElement
      ? root.matches('input, textarea')
        ? root
        : (root.querySelector('input, textarea') as HTMLInputElement | HTMLTextAreaElement | null)
      : null
  if (el) el.value = previous
}

const onInput = (value: string | number) => {
  const previous = String(props.modelValue ?? '')
  if (!acceptTextFieldFormatInput(props.platformFormat, value)) {
    restoreNativeInputValue(previous)
    emit('update:modelValue', props.modelValue ?? '')
    return
  }
  emit('update:modelValue', value)
}

const getCurrentInputValue = (): string | number => {
  const inputEl = inputRef.value?.$el?.querySelector?.('input, textarea') as
    | HTMLInputElement
    | HTMLTextAreaElement
    | null
    | undefined
  return inputEl ? inputEl.value : props.modelValue ?? ''
}

const onFocus = () => {
  isFocused.value = true
  emit('focus')

  if (isAutocompleteEnabled.value) {
    showDropdown.value = true
  }
}

const suggestionsItems = computed(() =>
  (props?.suggestions ?? []).map((row: any) => ({
    name: row.fields?.name?.value || listboxOptionTextFromRow(row) || '',
    extra: row.fields?.extra?.value || '',
  })),
)

const closeAutocompleteDropdown = () => {
  showDropdown.value = false
}

const handleDocumentPointerDown = (event: PointerEvent) => {
  if (!showDropdown.value) return
  const target = event.target as Node | null
  if (!target) return
  const inputRoot = inputRef.value?.$el as HTMLElement | undefined
  if (inputRoot?.contains(target)) return
  if (autocompleteDropdownRef.value?.contains(target)) return
  closeAutocompleteDropdown()
}

const onBlur = (event?: FocusEvent) => {
  isFocused.value = false
  emit('blur')

  const related = event?.relatedTarget as Node | null
  if (related && autocompleteDropdownRef.value?.contains(related)) {
    return
  }

  if (blurCloseTimer) clearTimeout(blurCloseTimer)
  blurCloseTimer = setTimeout(() => {
    blurCloseTimer = null
    closeAutocompleteDropdown()
  }, 0)
}

const onEscape = (event?: KeyboardEvent) => {
  if (!isAutocompleteEnabled.value || !showDropdown.value) return
  event?.preventDefault()
  closeAutocompleteDropdown()
}

const clearSelectOnlySuggester = () => {
  if (!isSelectOnlyAutocomplete.value || props.readonly || props.disabled) return
  if (String(props.modelValue ?? '').trim() === '') return
  closeAutocompleteDropdown()
  emit('update:modelValue', '')
  emit('change', '')
}

const onSelectOnlyDelete = (event: KeyboardEvent) => {
  if (!isSelectOnlyAutocomplete.value || props.readonly || props.disabled) return
  event.preventDefault()
  clearSelectOnlySuggester()
}
const onEnter = (event?: KeyboardEvent) => {
  event?.preventDefault()
  const value = getCurrentInputValue()
  emit('enter', value)
  emit('change', value)
}
const toggleTooltip = (event: Event) => {
  popover.value.toggle(event)
}

const isFinitePositiveLimit = computed(() => isFinitePositiveMultipleLimit(props.multipleLimit))

const showSelectAllRow = computed(() => isMultipleMode.value && !isFinitePositiveLimit.value)

const showAutocompleteDropdown = computed(() => {
  return isAutocompleteEnabled.value && showDropdown.value && suggestionsItems.value.length > 0
})

const selectedSuggestion = computed(() => {
  const currentValue = String(props.modelValue || '')
  if (isMultipleMode.value) {
    return currentValue.split(props?.multipleDelimiter ?? '').map(v => v.trim()).filter(Boolean)
  }
  return currentValue
})

const isSuggestionSelected = (suggestion: AutoCompleteSuggestion) => {
  return Array.isArray(selectedSuggestion.value) ? selectedSuggestion.value.find(name => name === suggestion.name) : suggestion.name === selectedSuggestion.value
}

const unselectedSuggestions = computed(() =>
  (suggestionsItems?.value ?? []).filter(s => !isSuggestionSelected(s)),
)

const ROW_PX = 52
const CELL_HEIGHT_PX = ROW_PX
const VIEW_MARGIN = 8
const MIN_CELL_WIDTH = 148
const MAX_CELL_WIDTH = 480
const CHAR_WIDTH_PX = 9
const CELL_PADDING_SWING = 30
/** Резерв для расчёта колонок (реальная строка + border) */
const SELECT_ALL_ROW_HEIGHT = 42
const DROPDOWN_CHROME_PX = 4
const MAX_ROWS_PER_COLUMN = 11

interface AutocompleteLayout {
  cellWidth: number
  rowsAmount: number
  columnsAmount: number
  panelMaxHeight: number
  /** Высота контента: «Выбрать все» + сетка + рамка */
  intrinsicHeight: number
  popupWidth: number
  popupIsUnder: boolean
}

const layout = ref<AutocompleteLayout>({
  cellWidth: MIN_CELL_WIDTH,
  rowsAmount: 1,
  columnsAmount: 1,
  panelMaxHeight: ROW_PX * 8,
  intrinsicHeight: ROW_PX * 8,
  popupWidth: MIN_CELL_WIDTH,
  popupIsUnder: true,
})

const layoutRect = ref<{ anchorLeft: number } | null>(null)

function estimateCellWidthForSuggestions(list: AutoCompleteSuggestion[]): number {
  let max = MIN_CELL_WIDTH
  for (const s of list) {
    const nameLen = (s.name || '').length
    const w = nameLen * CHAR_WIDTH_PX + CELL_PADDING_SWING
    max = Math.max(max, Math.min(MAX_CELL_WIDTH, w))
  }
  return Math.min(MAX_CELL_WIDTH, Math.max(MIN_CELL_WIDTH, max))
}

function formatSuggestionExtra(extra?: string): string {
  if (extra == null || extra === '' || extra === 'null') return ''
  return extra.length > 85 ? `${extra.slice(0, 85)}...` : extra
}

function getClippedVerticalSpace(fieldEl: HTMLElement, downward: boolean): number {
  const rect = fieldEl.getBoundingClientRect()
  let space = downward
    ? window.innerHeight - rect.bottom - VIEW_MARGIN
    : rect.top - VIEW_MARGIN
  space = Math.max(ROW_PX * 2, space)

  let parent: HTMLElement | null = fieldEl.parentElement
  while (parent && parent !== document.documentElement) {
    const st = getComputedStyle(parent)
    const oy = st.overflowY
    if (oy === 'auto' || oy === 'scroll') {
      const pr = parent.getBoundingClientRect()
      if (downward) {
        const below = pr.bottom - rect.bottom - VIEW_MARGIN
        space = Math.min(space, Math.max(ROW_PX * 2, below))
      } else {
        const above = rect.top - pr.top - VIEW_MARGIN
        space = Math.min(space, Math.max(ROW_PX * 2, above))
      }
    }
    parent = parent.parentElement
  }
  return space
}

function updateAutocompleteLayout() {
  if (!showDropdown.value || !props.autoComplete) return

  const el = inputRef.value?.$el as HTMLElement | undefined
  if (!el || typeof el.getBoundingClientRect !== 'function') return

  const rect = el.getBoundingClientRect()
  const list = suggestionsItems.value
  const modelSize = list.length
  if (modelSize === 0) return

  const cellWidth = estimateCellWidthForSuggestions(list)
  const formW = document.documentElement.clientWidth
  const innerH = window.innerHeight

  const popupIsUnder = rect.top + rect.height / 2 <= innerH / 2
  const panelMaxHeight = Math.max(ROW_PX * 3, getClippedVerticalSpace(el, popupIsUnder))

  const selectAllH = showSelectAllRow.value ? SELECT_ALL_ROW_HEIGHT : 0
  const gridAreaBudget = Math.max(ROW_PX, panelMaxHeight - selectAllH)

  const maxColumns = Math.max(1, Math.floor((formW - VIEW_MARGIN * 2) / cellWidth))
  const maxRowsFromSpace = Math.max(1, Math.floor(gridAreaBudget / ROW_PX))
  const rowsPerColIdeal = Math.max(1, Math.min(maxRowsFromSpace, MAX_ROWS_PER_COLUMN))
  const requiredColumns = Math.ceil(modelSize / rowsPerColIdeal)
  const columnsAmount = Math.min(requiredColumns, maxColumns)
  const rowsAmount = Math.max(1, Math.ceil(modelSize / columnsAmount))

  const contentGridHeight = rowsAmount * ROW_PX
  const intrinsicHeight = selectAllH + contentGridHeight + DROPDOWN_CHROME_PX

  const contentWidth = columnsAmount * cellWidth
  const popupWidth = Math.min(
    Math.max(contentWidth, rect.width),
    formW - VIEW_MARGIN * 2,
  )

  let anchorLeft = rect.left
  if (anchorLeft + popupWidth > formW - VIEW_MARGIN) {
    anchorLeft = Math.max(VIEW_MARGIN, formW - VIEW_MARGIN - popupWidth)
  }
  if (anchorLeft < VIEW_MARGIN) anchorLeft = VIEW_MARGIN

  layout.value = {
    cellWidth,
    rowsAmount,
    columnsAmount,
    panelMaxHeight,
    intrinsicHeight,
    popupWidth,
    popupIsUnder,
  }
  layoutRect.value = { anchorLeft }
}

const dropdownContainerStyle = computed(() => {
  const el = inputRef.value?.$el as HTMLElement | undefined
  if (!el || typeof el.getBoundingClientRect !== 'function') {
    return {
      position: 'fixed' as const,
      visibility: 'hidden' as const,
      top: '0',
      left: '0',
      width: '0',
      zIndex: 12000,
    }
  }
  const L = layout.value
  const fresh = el.getBoundingClientRect()
  const anchorLeft = layoutRect.value?.anchorLeft ?? fresh.left
  const displayedH = Math.min(L.intrinsicHeight, L.panelMaxHeight)
  const totalH = displayedH + 6
  const topPx = L.popupIsUnder ? fresh.bottom + 2 : fresh.top - totalH - 2

  return {
    position: 'fixed' as const,
    top: `${Math.max(VIEW_MARGIN, topPx)}px`,
    left: `${anchorLeft}px`,
    width: `${L.popupWidth}px`,
    maxWidth: `calc(100vw - ${VIEW_MARGIN * 2}px)`,
    zIndex: 12000,
    visibility: 'visible' as const,
  }
})

const autocompleteGridStyle = computed(() => {
  const L = layout.value
  return {
    display: 'grid',
    gridAutoFlow: 'column' as const,
    gridAutoColumns: 'minmax(0, 1fr)',
    gridTemplateRows: `repeat(${L.rowsAmount}, ${ROW_PX}px)`,
    width: '100%',
    minWidth: 0,
  }
})

function onAutocompleteViewportChange() {
  if (showDropdown.value && props.autoComplete) {
    updateAutocompleteLayout()
  }
}

watch(showAutocompleteDropdown, async open => {
  if (open) {
    await nextTick()
    updateAutocompleteLayout()
  }
})

watch(suggestionsItems, () => {
  if (showAutocompleteDropdown.value) {
    nextTick(() => updateAutocompleteLayout())
  }
})

watch(showDropdown, open => {
  if (open) {
    document.addEventListener('pointerdown', handleDocumentPointerDown, true)
  } else {
    document.removeEventListener('pointerdown', handleDocumentPointerDown, true)
  }
})

onMounted(() => {
  if (!isAutocompleteEnabled.value) return
  window.addEventListener('resize', onAutocompleteViewportChange)
  window.addEventListener('scroll', onAutocompleteViewportChange, true)
})

onUnmounted(() => {
  if (showDropdown.value) {
    document.removeEventListener('pointerdown', handleDocumentPointerDown, true)
  }
  if (blurCloseTimer) {
    clearTimeout(blurCloseTimer)
    blurCloseTimer = null
  }
  if (!isAutocompleteEnabled.value) return
  window.removeEventListener('resize', onAutocompleteViewportChange)
  window.removeEventListener('scroll', onAutocompleteViewportChange, true)
})

const finishAutocompleteSelection = (newValue: string | number, selectedCount: number) => {
  emit('update:modelValue', newValue)
  emit('change', newValue)
  if (
    shouldCloseAutocompleteAfterSelect({
      isMultipleMode: isMultipleMode.value,
      multipleLimit: props.multipleLimit,
      selectedCountAfter: selectedCount,
    })
  ) {
    closeAutocompleteDropdown()
    blurInputField()
  } else {
    void nextTick(() => focusAutocompleteInput())
  }
}

const selectAll = () => {
  if (!unselectedSuggestions.value.length) return
  const currentValue = String(props.modelValue || '')
  const values = currentValue.split(props?.multipleDelimiter ?? '').map(v => v.trim()).filter(Boolean)
  if (isMultipleMode.value && isFinitePositiveLimit.value) {
    const cap = props.multipleLimit as number
    for (const unselectedItem of unselectedSuggestions.value) {
      if (values.length >= cap) break
      values.push(unselectedItem.name)
    }
  } else {
    for (const unselectedItem of unselectedSuggestions.value) {
      values.push(unselectedItem.name)
    }
  }
  const joined = values.join(props.multipleDelimiter ?? '')
  finishAutocompleteSelection(joined, values.length)
  closeAutocompleteDropdown()
}

const selectSuggestion = (suggestion: AutoCompleteSuggestion) => {
  if (isMultipleMode.value) {
    const currentValue = String(props.modelValue || '')
    const values = currentValue.split(props?.multipleDelimiter ?? '').map(v => v.trim()).filter(Boolean)
    const existingIdx = values.indexOf(suggestion.name)
    if (existingIdx >= 0) {
      values.splice(existingIdx, 1)
      const newValue = values.join(props.multipleDelimiter ?? '')
      finishAutocompleteSelection(newValue, values.length)
      return
    }
    if (
      isAtMultipleSelectionLimit({
        isMultipleMode: true,
        multipleLimit: props.multipleLimit,
        selectedCount: values.length,
      })
    ) {
      return
    }
    values.push(suggestion.name)
    const newValue = values.join(props.multipleDelimiter ?? '')
    finishAutocompleteSelection(newValue, values.length)
    return
  }
  finishAutocompleteSelection(suggestion.name, 1)
}

function blurInputField() {
  const root = inputRef.value?.$el as HTMLElement | undefined
  if (!root) return
  const ta = root.querySelector?.('textarea')
  if (ta) (ta as HTMLTextAreaElement).blur()
  else root.blur?.()
}

function focusAutocompleteInput(): boolean {
  const root = inputRef.value?.$el as HTMLElement | undefined
  if (!root) return false
  const input = root.querySelector?.('input, textarea') as HTMLInputElement | HTMLTextAreaElement | null
  if (input) {
    input.focus()
    return true
  }
  return false
}

/** SwingText.openSuggester — показать dropdown после загрузки suggestions с сервера. */
function openAutocompleteDropdown() {
  if (!isAutocompleteEnabled.value) return
  if (suggestionsItems.value.length === 0) return
  showDropdown.value = true
  focusAutocompleteInput()
  void nextTick(() => updateAutocompleteLayout())
}

defineExpose({
  openAutocompleteDropdown,
  refreshMask: () => inputRef.value?.updateValue?.(true),
})
</script>

<style scoped lang="scss">
.m-input-wrapper {
  position: relative;
  width: 100%;

  &--select-only-clear {
    :deep(.p-inputtext),
    :deep(textarea.m-input) {
      padding-right: 1.75rem;
    }
  }

  :deep(textarea.m-input.m-input--autocomplete-multiple) {
    box-sizing: border-box;
    width: 100%;
    min-height: 40px !important;
    padding: 0.5rem 0.75rem;
    font-size: 14px;
    line-height: 1.5;
    resize: none;
    overflow-y: hidden;
    border-radius: 4px;
    background: #ffffff !important;
    border: 1px solid #d1d5db !important;
    color: #374151 !important;

    &::placeholder {
      color: rgba(0, 0, 0, 0.38);
    }

    &:focus {
      border-color: var(--primary-color, #1e3a6e) !important;
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-color, #1e3a6e) 10%, transparent) !important;
    }

    &:disabled,
    &[readonly] {
      opacity: 1 !important;
      color: #4b5563 !important;
      -webkit-text-fill-color: #4b5563;
      font-size: 14px;
      line-height: 1.5;
      background: #f9fafb !important;
    }
  }

  :deep(textarea.m-input.m-input--autocomplete-multiple.text-align-center) {
    text-align: center;
  }

  :deep(textarea.m-input.m-input--autocomplete-multiple.text-align-right) {
    text-align: right;
  }

  :deep(input.m-input.m-input--error.p-inputtext),
  :deep(textarea.m-input.m-input--error) {
    border-color: #e24c4c !important;
  }
}

.m-input {
  width: 100%;
  background-color: #ffffff !important;

  :deep(.p-inputtext),
  &.p-inputtext {
    width: 100%;
    min-height: 40px;
    padding: 0.5rem 0.75rem;
    font-size: 14px;
    border-radius: 4px;
    background: #ffffff !important;
    background-color: #ffffff !important;
    border: 1px solid #d1d5db !important;
    color: #374151 !important;

    &::placeholder {
      color: rgba(0, 0, 0, 0.38);
    }

    &:focus {
      border-color: var(--primary-color, #1e3a6e) !important;
      box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary-color, #1e3a6e) 10%, transparent) !important;
      background: #ffffff !important;
      background-color: #ffffff !important;
    }

    &:disabled,
    &[readonly] {
      opacity: 1 !important;
      color: #4b5563 !important;
      -webkit-text-fill-color: #4b5563;
      font-size: 14px;
      background: #f9fafb !important;
      background-color: #f9fafb !important;
    }
  }

  // Выравнивание текста
  &.text-align-center {
    :deep(.p-inputtext),
    &.p-inputtext {
      text-align: center;
    }
  }

  &.text-align-right {
    :deep(.p-inputtext),
    &.p-inputtext {
      text-align: right;
    }
  }

}

.m-input-suggester-clear {
  position: absolute;
  right: 4px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 2;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: 0;
  border-radius: 4px;
  background: transparent;
  color: #9ca3af;
  cursor: pointer;

  &:hover {
    color: #374151;
    background: #f3f4f6;
  }

  .pi {
    font-size: 11px;
  }
}

.m-input-wrapper:has(.m-input-tooltip) .m-input-suggester-clear {
  right: 2rem;
}

.m-input-tooltip {
  position: absolute;
  right: 0.5rem;
  top: 50%;
  transform: translateY(-50%);

  .tooltip-button {
    width: 24px;
    height: 24px;
    color: #6b7280 !important;

    &:hover {
      color: #374151 !important;
    }
  }
}

.m-input-hint {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
  padding: 0 0.5rem;
}


.m-input-autocomplete-dropdown {
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  overflow: hidden;

  .autocomplete-dropdown-body {
    flex: 1 1 auto;
    min-height: 0;
    min-width: 0;
    width: 100%;
    box-sizing: border-box;
    overflow-x: hidden;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
  }

  .autocomplete-select-all {
    position: sticky;
    top: 0;
    z-index: 1;
    flex-shrink: 0;
    box-sizing: border-box;
    padding: 0.5rem 0.75rem;
    cursor: pointer;
    font-size: 13px;
    font-weight: 700;
    text-align: center;
    color: #1e40af;
    border-bottom: 1px solid #e5e7eb;
    background: #ffffff;
    transition: background-color 0.15s ease;

    &:hover {
      background-color: #eff6ff;
    }
  }

  .autocomplete-grid {
    box-sizing: border-box;
  }

  .autocomplete-item {
    box-sizing: border-box;
    min-width: 0;
    width: 100%;
    height: 100%;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    padding: 0.25rem 0.4rem;
    cursor: pointer;
    font-size: 14px;
    line-height: 1.15;
    color: #374151;
    border: 1px solid #d1d5db;
    margin: 0;
    background: #fff;
    transition: background-color 0.12s ease;

    &:hover {
      background-color: #f3f4f6;
    }

    &:active {
      background-color: #e5e7eb;
    }

    &--selected {
      background-color: #dbeafe;
      border-color: #93c5fd;

      &:hover {
        background-color: #bfdbfe;
      }
    }
  }

  .autocomplete-item-name {
    max-width: 100%;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    line-clamp: 2;
    word-break: break-word;
  }

  .autocomplete-item-extra {
    margin-top: 0.1rem;
    font-size: 11px;
    color: #6b7280;
    line-height: 1.1;
    max-width: 100%;
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 1;
    line-clamp: 1;
    word-break: break-word;
  }
}

// Адаптивность
@media (max-width: 768px) {
  .m-input {
    :deep(.p-inputtext),
    &.p-inputtext {
      font-size: 16px; // Улучшает UX на мобильных
    }
  }
}

/* Стык с element-actions-right: плоский правый край поля */
:global(.element-content-wrapper:has(.element-actions-right)) .m-input-wrapper {
  :deep(.p-inputtext),
  :deep(textarea.m-input) {
    border-radius: 4px 0 0 4px !important;
  }
}

/* Для messageText с кнопкой справа устанавливаем фиксированную ширину */
:global(.element-renderer-fixed-width) .m-input-wrapper {
  width: 1100px !important;
}
</style>
